import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Globe, Mic, MessagesSquare, Languages, ArrowRight, Check, Puzzle, GraduationCap, Zap, Inbox, BarChart3, Sparkles, Star, Building2, Stethoscope, ShoppingBag, Hotel, Truck } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { HeroDemo } from "@/components/HeroDemo";

export default function Landing() {
  const { t, lang, toggle } = useI18n();
  const navigate = useNavigate();

  const features = [
    { icon: Mic, title: t("feature1Title"), desc: t("feature1Desc") },
    { icon: Languages, title: t("feature2Title"), desc: t("feature2Desc") },
    { icon: MessagesSquare, title: t("feature3Title"), desc: t("feature3Desc") },
  ];

  const en = lang === "en";
  const whatsNew = [
    { icon: Puzzle, tag: en ? "Extension" : "الإضافة", title: en ? "Translate inside WhatsApp Web" : "الترجمة داخل واتساب ويب", desc: en ? "Inline text & voice-note translation right on WhatsApp Web — no copy-paste." : "ترجمة النص والرسائل الصوتية مباشرة داخل واتساب ويب — دون نسخ ولصق." },
    { icon: GraduationCap, tag: en ? "New" : "جديد", title: en ? "Learn Arabic as you translate" : "تعلّم العربية أثناء الترجمة", desc: en ? "Every translation builds your phrasebook with pronunciation you can practice." : "كل ترجمة تبني دفتر عباراتك مع نطق يمكنك التدرّب عليه." },
    { icon: Zap, tag: en ? "New" : "جديد", title: en ? "Daily quizzes & AI role-play" : "اختبارات يومية ومحادثات تدريبية بالذكاء الاصطناعي", desc: en ? "Spaced-repetition quizzes and realistic customer role-plays that score you." : "اختبارات بالتكرار المتباعد ومحادثات عملاء واقعية تقيّم أداءك." },
    { icon: Inbox, tag: "CRM", title: en ? "Business inbox with CRM" : "صندوق أعمال مع CRM", desc: en ? "Multi-agent inbox, tags, notes, saved replies, glossary & smart-reply drafts." : "صندوق متعدد الوكلاء، وسوم، ملاحظات، ردود محفوظة، مصطلحات ومسودات ذكية." },
    { icon: BarChart3, tag: "BI", title: en ? "Team analytics" : "تحليلات الفريق", desc: en ? "See top phrases, translation volume, and per-agent performance at a glance." : "شاهد أكثر العبارات، حجم الترجمة، وأداء كل وكيل بلمحة." },
    { icon: Languages, tag: en ? "Core" : "أساسي", title: en ? "Gulf-dialect accurate" : "دقّة اللهجة الخليجية", desc: en ? "Meaning-first Arabic⇄English built for real GCC conversations." : "ترجمة عربية⇄إنجليزية تركّز على المعنى ومصممة لمحادثات الخليج الحقيقية." },
  ];

  const metrics = [
    { value: "12,000+", label: en ? "messages translated" : "رسالة مُترجمة" },
    { value: "45%", label: en ? "faster replies" : "ردود أسرع" },
    { value: "4.8★", label: en ? "from GCC teams" : "من فرق الخليج" },
    { value: en ? "30s" : "٣٠ث", label: en ? "to first translation" : "لأول ترجمة" },
  ];

  const trustedBy = [
    { icon: Building2, name: "Al Noor Realty" },
    { icon: Stethoscope, name: "Gulf Care Clinics" },
    { icon: ShoppingBag, name: "Souq Express" },
    { icon: Hotel, name: "Marina Stays" },
    { icon: Truck, name: "Falcon Logistics" },
  ];

  const testimonials = [
    {
      quote: en ? "GulfBridge cut our response time in half. My agents finally reply to Arabic customers with confidence." : "خفّض GulfBridge وقت ردّنا إلى النصف. أصبح وكلائي يردّون على العملاء العرب بثقة.",
      name: "Layla H.", role: en ? "Operations Lead · Real Estate, Dubai" : "مديرة العمليات · عقارات، دبي", initials: "LH", color: "bg-primary",
    },
    {
      quote: en ? "The WhatsApp extension is magic — inline translation right where we already work, no copy-paste." : "إضافة واتساب رائعة — ترجمة مباشرة في مكان عملنا، دون نسخ ولصق.",
      name: "Omar S.", role: en ? "Support Manager · E-commerce, Riyadh" : "مدير الدعم · تجارة إلكترونية، الرياض", initials: "OS", color: "bg-accent",
    },
    {
      quote: en ? "My team is actually learning Arabic while they work. The daily quiz is a real hit." : "فريقي يتعلّم العربية فعلاً أثناء العمل. الاختبار اليومي أحبّه الجميع.",
      name: "Sara M.", role: en ? "Clinic Manager · Healthcare, Doha" : "مديرة عيادة · رعاية صحية، الدوحة", initials: "SM", color: "bg-primary",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="max-w-7xl mx-auto px-5 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-primary grid place-items-center text-white">
            <Languages size={22} />
          </div>
          <span className="font-head font-bold text-xl text-primary">{t("appName")}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggle} data-testid="landing-lang-toggle" className="flex items-center gap-1.5 h-10 px-3 rounded-full border border-border text-sm font-medium hover:bg-secondary">
            <Globe size={16} /> {lang === "en" ? "العربية" : "EN"}
          </button>
          <Button variant="ghost" onClick={() => navigate("/login")} data-testid="landing-login-btn">{t("login")}</Button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 pt-8 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-pop">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-accent bg-accent/10 px-3 py-1.5 rounded-full mb-6">
            🌍 {t("tagline")}
          </span>
          <h1 className="font-head font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground leading-[1.05]">
            {t("heroTitle")}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-xl leading-relaxed">
            {t("heroSub")}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Button
              size="lg"
              onClick={() => navigate("/register")}
              data-testid="landing-get-started-btn"
              className="h-12 px-7 text-base rounded-full bg-accent hover:bg-accent/90"
            >
              {t("getStarted")} <ArrowRight size={18} className="rtl:rotate-180" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/login")} className="h-12 px-7 text-base rounded-full">
              {t("login")}
            </Button>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-7 text-sm text-muted-foreground">
            {["Arabic ⇄ English", "Gulf dialects", "Voice notes", "Business inbox"].map((x) => (
              <span key={x} className="flex items-center gap-1.5"><Check size={15} className="text-accent" /> {x}</span>
            ))}
          </div>
        </div>
        <div className="relative">
          <HeroDemo />
        </div>
      </section>

      {/* Metrics bar */}
      <section className="max-w-5xl mx-auto px-5 -mt-4 mb-4" data-testid="metrics-bar">
        <div className="bg-white border border-border rounded-2xl shadow-sm grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-border rtl:md:divide-x-reverse">
          {metrics.map((m, i) => (
            <div key={i} className="px-4 py-5 text-center" data-testid={`metric-${i}`}>
              <div className="font-head font-bold text-2xl sm:text-3xl text-primary">{m.value}</div>
              <div className="text-xs sm:text-sm text-muted-foreground mt-1">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Social proof — trusted by */}
      <section className="border-y border-border bg-secondary/30" data-testid="trusted-by">
        <div className="max-w-7xl mx-auto px-5 py-8">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-5">
            {en ? "Trusted by customer teams across the GCC" : "موثوق به من فرق خدمة العملاء في دول الخليج"}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {trustedBy.map(({ icon: Icon, name }) => (
              <div key={name} className="flex items-center gap-2 text-muted-foreground/70 hover:text-foreground transition-colors">
                <Icon size={20} />
                <span className="font-head font-semibold text-[15px] tracking-tight">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-5 py-20">
          <h2 className="font-head font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-center text-foreground">
            {lang === "en" ? "Built for everyday Gulf communication" : "مصمم للتواصل اليومي في الخليج"}
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {features.map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="bg-white rounded-2xl border border-border p-7 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center mb-5">
                  <Icon size={24} />
                </div>
                <h3 className="font-head font-semibold text-xl text-foreground">{title}</h3>
                <p className="text-muted-foreground mt-2 leading-relaxed text-[15px]">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's new */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent bg-accent/10 px-3 py-1.5 rounded-full">
            <Sparkles size={15} /> {en ? "What's new" : "الجديد لدينا"}
          </span>
          <h2 className="font-head font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground mt-4">
            {en ? "More than translation — a full GCC comms toolkit" : "أكثر من مجرد ترجمة — منظومة تواصل خليجية متكاملة"}
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto text-[15px]">
            {en ? "Your team communicates, closes leads, and gets better at Arabic — all in one place." : "فريقك يتواصل، يكسب العملاء، ويتحسّن في العربية — كل ذلك في مكان واحد."}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12" data-testid="whats-new-grid">
          {whatsNew.map(({ icon: Icon, tag, title, desc }, i) => (
            <div key={i} className="relative bg-white rounded-2xl border border-border p-7 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`whats-new-${i}`}>
              <span className="absolute top-5 end-5 text-[10px] font-bold uppercase tracking-wide text-accent bg-accent/10 px-2 py-0.5 rounded-full">{tag}</span>
              <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center mb-5">
                <Icon size={24} />
              </div>
              <h3 className="font-head font-semibold text-lg text-foreground pe-14">{title}</h3>
              <p className="text-muted-foreground mt-2 leading-relaxed text-[15px]">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-secondary/40 border-y border-border" data-testid="testimonials">
        <div className="max-w-7xl mx-auto px-5 py-20">
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent bg-accent/10 px-3 py-1.5 rounded-full">
              <Star size={15} className="fill-current" /> {en ? "Loved by GCC teams" : "محبوب لدى فرق الخليج"}
            </span>
            <h2 className="font-head font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground mt-4">
              {en ? "Real results, in Arabic and English" : "نتائج حقيقية، بالعربية والإنجليزية"}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {testimonials.map((tst, i) => (
              <div key={i} className="bg-white rounded-2xl border border-border p-7 flex flex-col" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 text-accent mb-4">
                  {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={15} className="fill-current" />)}
                </div>
                <p className="text-foreground leading-relaxed text-[15px] flex-1">“{tst.quote}”</p>
                <div className="flex items-center gap-3 mt-6">
                  <div className={`h-11 w-11 rounded-full ${tst.color} text-white grid place-items-center font-bold`}>{tst.initials}</div>
                  <div className="leading-tight">
                    <div className="font-semibold text-foreground text-sm">{tst.name}</div>
                    <div className="text-xs text-muted-foreground">{tst.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-5 py-20 text-center">
        <h2 className="font-head font-bold text-3xl lg:text-4xl text-foreground tracking-tight">
          {lang === "en" ? "Start translating in seconds." : "ابدأ الترجمة في ثوانٍ."}
        </h2>
        <Button size="lg" onClick={() => navigate("/register")} className="h-12 px-8 mt-7 rounded-full bg-accent hover:bg-accent/90 text-base">
          {t("getStarted")}
        </Button>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        © 2026 {t("appName")} · {t("tagline")}
      </footer>
    </div>
  );
}
